import { Icon } from '@natera/material/lib/icon'
import { useStore } from '@tanstack/react-form'
import { format, parse } from 'date-fns'
import { Collapsible } from 'radix-ui'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { formatCurrency } from '~/shared/lib/currency'
import { useAppForm } from '~/shared/lib/form'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import { Button, CollapsibleContent, DatePicker, Text, Textfield, Tooltip } from '~/shared/ui'
import {
  buildDefaultValues,
  buildRequestBody,
  computeLeftToAllocate,
  isClaimValid,
} from '../../lib'
import { createEmptyServiceLine } from '../../model'
import { ServiceLinesTable } from './service-lines-table/service-lines-table'

export interface PatientClaimFormRef {
  getRequestBody: () => apiClient.PatientClaimCreateRequest
  resetToServerResponse: (claim: apiClient.PatientClaimResponse) => void
  undo: () => void
  redo: () => void
  setLocalFlag: (flag: { notes: string } | null) => void
}

interface PatientClaimFormProps {
  claim: apiClient.PatientClaimResponse
  onNameChange?: (firstName: string, lastName: string) => void
  onModifiedChange?: (isDirty: boolean) => void
  onValidChange?: (isValid: boolean) => void
  onCanUndoChange?: (canUndo: boolean) => void
  onCanRedoChange?: (canRedo: boolean) => void
  onLocalFlagChange?: (localFlag: { notes: string } | null) => void
}

export const PatientClaimForm = forwardRef<PatientClaimFormRef, PatientClaimFormProps>(
  function PatientClaimForm(
    {
      claim,
      onNameChange,
      onModifiedChange,
      onValidChange,
      onCanUndoChange,
      onCanRedoChange,
      onLocalFlagChange,
    },
    ref,
  ) {
    const defaultValues = buildDefaultValues(claim)

    const form = useAppForm({
      defaultValues,
    })

    const {
      set: setHistoryValues,
      reset: resetHistoryValues,
      undo,
      redo,
      canUndo,
      canRedo,
    } = useUndoRedo({
      initialValues: defaultValues,
      setFieldValue: form.setFieldValue,
    })

    useImperativeHandle(ref, () => ({
      getRequestBody: () => buildRequestBody(form.state.values),
      resetToServerResponse: (claim: apiClient.PatientClaimResponse) => {
        const newDefaults = buildDefaultValues(claim)
        form.reset(newDefaults)
        resetHistoryValues(newDefaults)
      },
      undo,
      redo,
      setLocalFlag: (flag: { notes: string } | null) => {
        form.setFieldValue('localFlag', flag)
        setHistoryValues({ localFlag: flag })
      },
    }))

    // Notify parent of form state changes
    const firstName = useStore(form.store, (s) => s.values.patientFirstName)
    const lastName = useStore(form.store, (s) => s.values.patientLastName)
    useEffect(() => {
      onNameChange?.(firstName, lastName)
    }, [firstName, lastName, onNameChange])

    const isDefaultValue = useStore(form.store, (s) => s.isDefaultValue)
    useEffect(() => {
      onModifiedChange?.(!isDefaultValue)
    }, [isDefaultValue, onModifiedChange])

    const isValid = useStore(form.store, (s) => isClaimValid(s.values))
    useEffect(() => {
      onValidChange?.(isValid)
    }, [isValid, onValidChange])

    useEffect(() => {
      onCanUndoChange?.(canUndo)
    }, [canUndo, onCanUndoChange])
    useEffect(() => {
      onCanRedoChange?.(canRedo)
    }, [canRedo, onCanRedoChange])

    const localFlag = useStore(form.store, (s) => s.values.localFlag)
    useEffect(() => {
      onLocalFlagChange?.(localFlag)
    }, [localFlag, onLocalFlagChange])

    return (
      <>
        <Collapsible.Root
          className="relative px-3"
          defaultOpen
        >
          <div className="border-divider rounded-lg border">
            <Collapsible.Trigger className="group hover:bg-action-hovered flex w-full cursor-pointer items-center justify-between gap-1 border-none bg-transparent px-3 py-2.5 transition-colors">
              <Text
                className="text-text-primary"
                appearance="BODY2"
              >
                Patient Details
              </Text>
              <Icon
                className="text-action-active group-data-[state=closed]:rotate-180"
                children="keyboard_arrow_up"
              />
            </Collapsible.Trigger>

            <CollapsibleContent>
              <div className="flex flex-col gap-2 px-3 pb-3">
                <div className="grid grid-cols-2 gap-4">
                  <form.Field name="patientAccountNumber">
                    {(field) => (
                      <div className="relative">
                        <Tooltip content="Enter all numbers and letters as shown.">
                          <Icon
                            children="info"
                            className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
                          />
                        </Tooltip>
                        <Textfield
                          label="Patient Account Number"
                          leadingIcon={<span />}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={() => {
                            field.handleBlur()
                            setHistoryValues({ patientAccountNumber: field.state.value })
                          }}
                          required
                        />
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="totalPaid">
                    {(field) => (
                      <Textfield
                        label="Total Paid"
                        currency
                        value={field.state.value ?? undefined}
                        onCentsChange={(cents) => field.handleChange(cents)}
                        onBlur={() => {
                          field.handleBlur()
                          setHistoryValues({ totalPaid: field.state.value })
                        }}
                        required
                      />
                    )}
                  </form.Field>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <form.Field name="patientFirstName">
                    {(field) => (
                      <Textfield
                        label="Patient First Name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={() => {
                          field.handleBlur()
                          setHistoryValues({ patientFirstName: field.state.value })
                        }}
                        required
                      />
                    )}
                  </form.Field>
                  <form.Field name="patientLastName">
                    {(field) => (
                      <Textfield
                        label="Patient Last Name"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={() => {
                          field.handleBlur()
                          setHistoryValues({ patientLastName: field.state.value })
                        }}
                        required
                      />
                    )}
                  </form.Field>
                  <form.Field name="claimNumber">
                    {(field) => (
                      <Textfield
                        label="Claim Number"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={() => {
                          field.handleBlur()
                          setHistoryValues({ claimNumber: field.state.value })
                        }}
                        required
                      />
                    )}
                  </form.Field>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <form.Field name="patientDob">
                    {(field) => (
                      <DatePicker
                        label="Date of Birth"
                        date={
                          field.state.value
                            ? parse(field.state.value, 'yyyy-MM-dd', new Date())
                            : undefined
                        }
                        onDateChange={(date) => {
                          const value = date ? format(date, 'yyyy-MM-dd') : ''
                          field.handleChange(value)
                          setHistoryValues({ patientDob: value })
                        }}
                      />
                    )}
                  </form.Field>
                  <form.Field name="memberId">
                    {(field) => (
                      <Textfield
                        label="Member Number"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={() => {
                          field.handleBlur()
                          setHistoryValues({ memberId: field.state.value })
                        }}
                      />
                    )}
                  </form.Field>
                  <form.Field name="pageOfDoc">
                    {(field) => (
                      <Textfield
                        label="Page of the Doc"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={() => {
                          field.handleBlur()
                          setHistoryValues({ pageOfDoc: field.state.value })
                        }}
                        required
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            </CollapsibleContent>
          </div>
          <Collapsible.Trigger className="group text-content-medium-emphasis absolute top-full left-3 flex cursor-pointer items-center gap-2 border-none bg-transparent px-0 py-2 data-[state=closed]:hidden">
            <Icon
              className="group-data-[state=closed]:rotate-180"
              children="keyboard_arrow_up"
            />
            <Text appearance="BODY2">Collapse Patient Details</Text>
          </Collapsible.Trigger>
        </Collapsible.Root>

        <form.Subscribe
          selector={(s) => {
            const totalPaid = s.values.totalPaid
            const leftToAllocateCents = computeLeftToAllocate(totalPaid, s.values.serviceLines)
            return { totalPaid, leftToAllocateCents }
          }}
        >
          {({ totalPaid, leftToAllocateCents }) => {
            const totalDollars = totalPaid != null ? totalPaid / 100 : null
            const leftToAllocate = leftToAllocateCents != null ? leftToAllocateCents / 100 : null
            const leftToAllocateColor =
              leftToAllocate === 0
                ? 'text-chip-status-success-content'
                : 'text-chip-status-warning-content'

            return (
              <div className="flex justify-end p-3">
                <div className="flex items-center gap-2">
                  <Text
                    className="text-text-secondary"
                    appearance="CAPTION"
                  >
                    Total paid: {totalDollars != null ? formatCurrency(totalDollars) : '$ --'}
                  </Text>
                  <Text
                    className={leftToAllocateColor}
                    appearance="CAPTION"
                  >
                    Left to allocate:{' '}
                    {leftToAllocate != null ? formatCurrency(leftToAllocate) : '$ --'}
                  </Text>
                </div>
              </div>
            )
          }}
        </form.Subscribe>

        <form.Field
          name="serviceLines"
          mode="array"
        >
          {(field) => (
            <>
              <ServiceLinesTable
                form={form}
                serviceLineCount={field.state.value.length}
                onRemove={(index) => {
                  const newLines = form.state.values.serviceLines.filter((_, i) => i !== index)
                  field.removeValue(index)
                  setHistoryValues({ serviceLines: newLines })
                }}
                setHistoryValues={setHistoryValues}
              />

              <div className="mx-2 my-1">
                <Button
                  className="w-full"
                  size="small"
                  materialIcon="add"
                  onClick={() => {
                    const newLine = createEmptyServiceLine()
                    const newLines = [...form.state.values.serviceLines, newLine]
                    field.pushValue(newLine)
                    setHistoryValues({ serviceLines: newLines })
                  }}
                >
                  Service Line
                </Button>
              </div>
            </>
          )}
        </form.Field>
      </>
    )
  },
)
